import {CultureMatchItem, Salary} from '../features/applicant/dashboard/types/ApplicantMatchesTypes';
import {ProfileWorkExperience} from '../features/applicant/profile/types';
import {
  CultureType,
  CultureTypeJSON,
  TeamValue,
  TeamValueJSON,
} from '../features/company/culture/dashboard/types/CultureInformationTypes';
import {
  CompanyJobPostingMatches,
  CompanyJobPostingMatchesJSON,
} from '../features/company/recruiting/inbox/types/InboxTypes';

import {RequiredLanguageProfile} from './shared-types';

// Shared types

export type MatchStatusTab = 'active' | 'rejected' | 'hired' | 'on_hold';
export type CamelCaseMatchStatusTab = 'active' | 'rejected' | 'hired' | 'onHold';

export type MatchTotalCountPerStatus = {
  accepted: number;
  contacted: number;
  interview: number;
  hired: number;
  rejected: number;
  onHold?: number;
};

/** `GET .../matches/summary` payload under `company_matches_summary` after camelCase. */
export type CompanyMatchesSummaryResponse = {
  companyMatchesSummary: MatchTotalCountPerStatus;
};

type LanguageProfileRecruiting = {
  en?: string;
  de?: string;
};

export type MatchTagResponse = {
  matchTag: SupportedTagBE;
};

export type SupportedTag = {
  resourceId?: string;
  id: string;
  label: RequiredLanguageProfile;
};
export type MatchText = {
  low: RequiredLanguageProfile;
  mid: RequiredLanguageProfile;
  high: RequiredLanguageProfile;
};

// --- Match Information Types ---

export type MatchInformationOverview = {
  overallScore: number;
  culturalMatchScore?: number;
  jobMatchScore?: number;
  assessmentScore?: number;
};

export type JobMatchInformation = {
  score: number;
  matchText: RequiredLanguageProfile;
};

export type AssessmentAnswerChoice = {
  text: string;
  selectedByApplicant: boolean;
};

export type AssessmentQuestion = {
  question: string;
  type: 'single_choice' | 'multi_choice' | 'text';
  answers: AssessmentAnswerChoice[] | string[];
};

export type AssessmentResultsInformation = {
  score: number;
  resultText: RequiredLanguageProfile;
  questions: AssessmentQuestion[];
};

export type MatchDataResponse = {
  companyMatch: MatchData;
};

export type MatchData = {
  resourceId: string;
  updatedAt: string;
  createdAt: string;
  desiredSalary?: string;
  bestAddress?: string;
  campaignName: string;
  status: string;
  culturalFitScore?: string;
  culturalFitLevel?: string;
  tags: SupportedTagBE[];
  applicant: ApplicantRecruiting;
  matchmakerNotes?: string;
  companyMatchNotes?: string;
  companyJobPosting?: CompanyJobPostingMatches;
  rejectionInfo?: MatchRejectionInfo;
  items?: CultureMatchItem[];
  allItems?: CultureMatchItem[];
  matchInformationOverview?: MatchInformationOverview;
  jobMatchInformation?: JobMatchInformation;
  assessmentResultsInformation?: AssessmentResultsInformation;
};

export type SupportedTagBE = {
  resourceId: string;
  tagLabel: string;
};

export type ApplicantRecruiting = {
  resourceId: string;
  personalInformation: PersonalInformation;
  keyFacts: KeyFacts;
  profileTitle?: ProfileTitle;
  profilePictureUrl?: string;
  settings?: ProfileSettings;
  dreamJobDescription?: string;
  resumeUrl?: string;
  cultureType?: CultureType;
  skills?: Skill[];
  languages?: ApplicantLanguage[];
  workExperiences?: ProfileWorkExperience[];
};

export type ProfileTitle = {
  name: string;
  experience: ApplicantExperience;
};

export type ApplicantExperience = {
  amount: number;
  unit: string;
};

export type Skill = {
  resourceId: string;
  standardSkill: StandardSkill;
};

export type ProfileSettings = {
  jobSearchStatus: string;
};

export type StandardSkill = {
  createdAt: string;
  name: RequiredLanguageProfile;
  resourceId: string;
  updatedAt: string;
};

/** Language / salary fit rows from matches `key_facts` (AI-style insights). */
export type KeyFactInsight = {
  score: number;
  category: string;
  explanation: string;
};

/** Location fit from `key_facts.location` (may include distance metadata from BE). */
export type KeyFactLocationInsight = KeyFactInsight & {
  inferredDistanceKm?: number;
  distanceSource?: string;
};

/** Culture summary from matches `key_facts.culture`. */
export type KeyFactCultureInsight = {
  cultureTypeName?: LanguageProfileRecruiting;
  /** Rich text team value copy (BE: `team_value` with `html_en` / `html_de`). */
  teamValue?: TeamValue;
  fitScorePercentage?: number;
  fitLevel?: string;
};

/** Relevant skills surfaced in match key facts (BE: `relevant_skills`). */
export type KeyFactRelevantSkill = {
  name: string;
  evidence: string;
};

export type KeyFacts = {
  cultureTypeName?: LanguageProfileRecruiting;
  professionalExperience?: ProfessionalExperience;
  leadershipExperience?: boolean;
  /** Legacy salary range, or match-board salary insight object. */
  salary?: Salary | KeyFactInsight;
  startDate?: string;
  highestDegree?: RequiredLanguageProfile;
  location?: string | KeyFactLocationInsight;
  language?: KeyFactInsight;
  culture?: KeyFactCultureInsight;
  /** Match-board list of skills with short evidence snippets. */
  relevantSkills?: KeyFactRelevantSkill[];
};

export const isKeyFactLocationInsight = (
  location: string | KeyFactLocationInsight | undefined
): location is KeyFactLocationInsight =>
  Boolean(location && typeof location === 'object' && 'explanation' in location);

export const isKeyFactsSalaryRange = (salary: Salary | KeyFactInsight | undefined): salary is Salary =>
  Boolean(salary && typeof salary === 'object' && 'min' in salary);

export type ProfessionalExperience = {
  duration: number;
  unit: string;
};

export type PersonalInformation = {
  resourceId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobilePhoneNumber: string;
  linkedinUrl?: string;
};

const normalizeApplicantResumeUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Resolves the applicant CV URL from API payloads: canonical `resumeUrl`,
 * snake_case `resume_url`, alternate `resumeURL`, or nested under personal information.
 */
export const getApplicantResumeUrl = (
  applicant: ApplicantRecruiting | null | undefined
): string | undefined => {
  if (!applicant) return undefined;
  const withSnake = applicant as ApplicantRecruiting & {resume_url?: string};
  const direct =
    normalizeApplicantResumeUrl(applicant.resumeUrl) ??
    normalizeApplicantResumeUrl(withSnake.resume_url);
  if (direct) return direct;
  const pi = applicant.personalInformation as
    | (PersonalInformation & {resumeUrl?: string; resume_url?: string})
    | undefined;
  if (pi) {
    const nested =
      normalizeApplicantResumeUrl(pi.resumeUrl) ??
      normalizeApplicantResumeUrl(pi.resume_url);
    if (nested) return nested;
  }
  return normalizeApplicantResumeUrl((applicant as {resumeURL?: string}).resumeURL);
};

export type ApplicantLanguage = {
  resourceId: string;
  isoCode: string;
  proficiency: string;
};

// --- Rejections ---

export type Rejection = {
  rejectionReasonKey: string;
  rejectionType: string;
  rejectionReasonMessage: RequiredLanguageProfile;
};

/** Rejection summary on match details when status is rejected. */
export type MatchRejectionInfo = {
  rejectedBy: string;
  rejectionReasonKey: string;
  rejectionReasonMessage: string;
  talentMessage?: string;
};

// JSON property types

export type MatchDataJSON = {
  resource_id: string;
  updated_at: string;
  status: string;
  created_at: string;
  campaign_name: string;
  desired_salary?: string;
  best_address?: string;
  tags: SupportedTagBEJSON[];
  applicant: ApplicantRecruitingJSON;
  company_job_posting?: CompanyJobPostingMatchesJSON;
  matchmaker_notes?: string;
  company_match_notes?: string;
};

export type SupportedTagBEJSON = {
  resource_id: string;
  tag_label: string;
};

export type ApplicantRecruitingClassJSON = {
  applicant: ApplicantRecruitingJSON;
};

export type ProfileSettingsJSON = {
  job_search_status: string;
};

export type ApplicantRecruitingJSON = {
  resource_id: string;
  personal_information: PersonalInformationJSON;
  key_facts: KeyFactsJSON;
  profile_title?: string;
  settings?: ProfileSettingsJSON;
  dream_job_description?: string;
  resume_url?: string;
  skill?: SkillJSON[];
};

export type SkillJSON = {
  resource_id: string;
  name: RequiredLanguageProfile;
  duration: DurationJSON;
};

export type DurationJSON = {
  amount: number;
  unit: string;
};

export type KeyFactInsightJSON = {
  score: number;
  category: string;
  explanation: string;
};

export type KeyFactLocationInsightJSON = KeyFactInsightJSON & {
  inferred_distance_km?: number;
  distance_source?: string;
};

export type KeyFactCultureInsightJSON = {
  culture_type_name?: LanguageProfileRecruiting;
  team_value?: TeamValueJSON;
  fit_score_percentage?: number;
  fit_level?: string;
};

export type KeyFactRelevantSkillJSON = {
  name: string;
  evidence: string;
};

export type KeyFactsJSON = {
  culture_type_name?: LanguageProfileRecruiting;
  culture_type?: CultureTypeJSON;
  professional_experience?: ProfessionalExperience;
  leadership_experience?: boolean;
  salary?: Salary | KeyFactInsightJSON;
  start_date?: string;
  highest_degree?: RequiredLanguageProfile;
  location?: string | KeyFactLocationInsightJSON;
  language?: KeyFactInsightJSON;
  culture?: KeyFactCultureInsightJSON;
  relevant_skills?: KeyFactRelevantSkillJSON[];
};

export type PersonalInformationJSON = {
  resource_id: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile_phone_number: string;
  linkedin_url?: string;
  resume_url?: string;
};
